import type { ResponsiveImageMedia } from '@/types/responsive-image';

/**
 * Build a srcset string from a variants map for a single format.
 * Returns empty string if the format has no entries.
 */
export function buildSrcset(variants: Record<number, string> | undefined): string {
    if (!variants) return '';
    return Object.entries(variants)
        .map(([width, url]) => `${url} ${width}w`)
        .join(', ');
}

/**
 * Whether a media object has at least one variant in either format.
 */
export function hasVariants(media: Pick<ResponsiveImageMedia, 'responsive_variants'>): boolean {
    const v = media.responsive_variants;
    if (!v) return false;
    return Object.keys(v.avif).length > 0 || Object.keys(v.webp).length > 0;
}

/**
 * Construct a variant URL by transforming an original URL string.
 * Used when only a string URL is available (no Media object), e.g. legacy
 * page-builder block configs. Returns undefined for non-CDN URLs or
 * unsupported extensions.
 *
 *   variantUrlFromString('https://cdnweb…/2026/03/abc.png', 480, 'avif')
 *   -> 'https://cdnweb…/2026/03/abc-w480.avif'
 */
export function variantUrlFromString(
    originalUrl: string,
    width: number,
    format: 'avif' | 'webp',
): string | undefined {
    const dot = originalUrl.lastIndexOf('.');
    if (dot < 0) return undefined;
    const base = originalUrl.slice(0, dot);
    return `${base}-w${width}.${format}`;
}

/**
 * Derive AVIF + WebP sibling URLs for a static CDN PNG (no width selection).
 * Used for the partner logo wall — siblings are produced by
 * `scripts/convert-partner-logos.php`.
 *
 *   logoPictureSources('https://cdnweb.../v3/foo.png')
 *   -> { avif: '…/foo.avif', webp: '…/foo.webp' }
 *
 * Returns undefined for non-.png URLs.
 */
export function logoPictureSources(pngUrl: string): { avif: string; webp: string } | undefined {
    if (!pngUrl.toLowerCase().endsWith('.png')) return undefined;
    const base = pngUrl.slice(0, -4);
    return { avif: `${base}.avif`, webp: `${base}.webp` };
}

/**
 * Intrinsic pixel dimensions for the v3 partner logo set on the CDN.
 * All sources are 80px tall; widths vary. Used to populate width/height
 * HTML attributes so browsers can reserve aspect-ratio-correct space
 * before the image loads (prevents CLS). CSS classes (h-8, h-16, h-4.5)
 * still control the final rendered size.
 */
const PARTNER_LOGO_WIDTHS: Record<string, number> = {
    'four-seasons-preferred-partners-trimmed': 146,
    'mandarin-oriental-fan-club-trimmed': 411,
    'shangri-la-luxury-circle-trimmed': 482,
    'peninsula-penclub-trimmed': 371,
    'belmond-bellini-club-trimmed': 283,
    'hyatt-prive-trimmed': 128,
    'noble-house-vip-select-trimmed': 318,
    'hilton-for-luxury-trimmed': 155,
    'rosewood-elite-trimmed': 389,
    'jumeirah-passport-to-luxury-trimmed': 232,
    'couture-by-langham-trimmed': 339,
    'ihg-destined-trimmed': 386,
    'accor-preferred-hera-trimmed': 163,
    'platinum-partner-trimmed': 219,
    'select-by-internova-trimmed': 253,
    'abcccra-premier-hotel-program-trimmed': 273,
    'travel-leaders-network-trimmed': 239,
    'trump-preferred-partner-trimmed': 309,
};

export function partnerLogoIntrinsicSize(url: string): { width: number; height: number } {
    // Extract the last path segment without extension.
    const last = url.split('/').pop() ?? '';
    const slug = last.replace(/\.[^.]+$/, '');
    return { width: PARTNER_LOGO_WIDTHS[slug] ?? 320, height: 80 };
}
